<template>
  <div
    class="register-view"
    :class="{ 'register-view--form-scroll': registerViewPreferScroll }"
  >
    <div class="register-shell">
      <div class="register-card register-card--standard">
        <div class="register-card-heading mb-4">
          <h4 class="register-title text-center mb-2">{{ headerTitle }}</h4>
          <p v-if="!showVerification" class="register-subtitle text-center mb-1">
            Paso {{ currentStepIndex + 1 }} de {{ steps.length }}
          </p>
          <p v-if="!showVerification" class="register-step-hint text-center mb-0">
            {{ stepHint }}
          </p>
        </div>

        <div v-if="!showVerification" class="register-progress mb-4" aria-hidden="true">
          <div class="register-progress__bar">
            <span class="register-progress__fill" :style="{ width: progressPercent + '%' }"></span>
          </div>
          <div class="register-progress__steps">
            <span
              v-for="(step, i) in steps"
              :key="step.key"
              class="register-progress__step"
              :class="{
                'is-active': step.key === currentStep,
                'is-done': i < currentStepIndex,
              }"
            >
              <span class="register-progress__step-num">{{ i + 1 }}</span>
              <span class="register-progress__step-label">{{ step.label }}</span>
            </span>
          </div>
        </div>

          <div v-if="showVerification" class="verification-form activation-sent">
            <div
              v-if="registrationWasResend"
              class="alert alert-success small text-black mb-4"
              role="status"
            >
              <p class="mb-0 fw-semibold">Ya tenés una cuenta con este correo pendiente de verificar.</p>
              <p class="mb-0 mt-2 small">
                Te reenviamos el link de activación. Revisá la bandeja de entrada y el spam.
              </p>
            </div>
            <div class="activation-sent__icon" aria-hidden="true">
              <i class="fas fa-envelope-open-text"></i>
            </div>
            <p class="text-center register-detail mb-2">Enviamos un link de activación a:</p>
            <p class="text-center register-detail mb-4 fw-semibold text-break">{{ registeredEmail || formData.email }}</p>
            <p class="activation-sent__copy mb-4">
              Abrí el correo y tocá el botón para activar tu cuenta. Al hacerlo vas a iniciar sesión automáticamente.
            </p>
            <div class="activation-sent__timer" role="status" aria-live="polite">
              Volvemos al inicio en <strong class="ms-3">{{ activationRedirectCountdown }}</strong>s
            </div>
            <button type="button" class="btn btn-reg-cta w-100 py-3 mt-4" @click="goHomeNow">
              Ir al inicio ahora
            </button>
          </div>

          <!-- Formulario de Registro -->
          <form v-else @submit.prevent="handleSubmit" class="needs-validation w-100" novalidate>

            <div v-if="serverFieldErrors._general" class="alert alert-danger mb-3 small">
              {{ serverFieldErrors._general }}
            </div>

            <div v-if="currentStep === 'personal'">
              <h5 class="register-panel-title">
                <i class="fas fa-id-badge me-2" aria-hidden="true"></i>Datos personales
              </h5>
              <div class="register-fields register-fields--tight-after-title mb-3">
                <div class="form-floating register-field">
                  <input
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': fieldInvalid('first_name') }"
                    id="first_name"
                    v-model="formData.first_name"
                    placeholder="Nombre"
                    required
                    @input="clearServerFieldError('first_name')"
                  >
                  <label for="first_name">
                    <i class="fas fa-user icon-reg me-2"></i>Nombre
                  </label>
                  <div v-if="fieldInvalid('first_name')" class="invalid-feedback d-block register-field-feedback">
                    {{ fieldErrorText('first_name') }}
                  </div>
                </div>
                <div class="form-floating register-field">
                  <input
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': fieldInvalid('last_name') }"
                    id="last_name"
                    v-model="formData.last_name"
                    placeholder="Apellido"
                    required
                    @input="clearServerFieldError('last_name')"
                  >
                  <label for="last_name">
                    <i class="fas fa-user icon-reg me-2"></i>Apellido
                  </label>
                  <div v-if="fieldInvalid('last_name')" class="invalid-feedback d-block register-field-feedback">
                    {{ fieldErrorText('last_name') }}
                  </div>
                </div>
                <div class="form-floating register-field">
                  <input
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': fieldInvalid('dni') }"
                    id="dni"
                    v-model="formData.dni"
                    placeholder="DNI o CUIT"
                    inputmode="numeric"
                    autocomplete="off"
                    required
                    @input="(e) => { formData.dni = e.target.value.replace(/\D/g, ''); clearServerFieldError('dni') }"
                  >
                  <label for="dni">
                    <i class="fas fa-id-card icon-reg me-2"></i>DNI / CUIT
                  </label>
                  <div v-if="fieldInvalid('dni')" class="invalid-feedback d-block register-field-feedback">
                    {{ fieldErrorText('dni') }}
                  </div>
                </div>
                <div class="form-floating register-field">
                  <input
                    type="tel"
                    class="form-control"
                    :class="{ 'is-invalid': fieldInvalid('phone') }"
                    id="phone"
                    v-model="formData.phone"
                    placeholder="Teléfono"
                    autocomplete="tel"
                    required
                    @input="clearServerFieldError('phone')"
                  >
                  <label for="phone">
                    <i class="fas fa-phone icon-reg me-2"></i>Teléfono
                  </label>
                  <div v-if="fieldInvalid('phone')" class="invalid-feedback d-block register-field-feedback">
                    {{ fieldErrorText('phone') }}
                  </div>
                </div>
              </div>
            </div>

            <div v-else-if="currentStep === 'account'">
              <h5 class="register-panel-title">
                <i class="fas fa-shield-alt me-2" aria-hidden="true"></i>Usuario y acceso
              </h5>
              <div class="register-fields register-fields--tight-after-title mb-3">
                <div class="form-floating register-field">
                    <input
                      type="text"
                      class="form-control"
                      :class="{ 'is-invalid': fieldInvalid('username') }"
                      id="username"
                      v-model="formData.username"
                      placeholder="Usuario"
                      required
                      @input="clearServerFieldError('username')"
                    >
                    <label for="username">
                      <i class="fas fa-user icon-reg me-2"></i>Usuario
                    </label>
                    <div v-if="fieldInvalid('username')" class="invalid-feedback d-block register-field-feedback">
                      {{ fieldErrorText('username') }}
                    </div>
                  </div>
                  <div class="form-floating register-field">
                    <input
                      type="email"
                      class="form-control"
                      :class="{ 'is-invalid': fieldInvalid('email') }"
                      id="email"
                      v-model="formData.email"
                      placeholder="Email"
                      required
                      @input="resetEmailExistsError()"
                    >
                    <label for="email">
                      <i class="fas fa-envelope icon-reg me-2"></i>Email
                    </label>
                    <div v-if="fieldInvalid('email')" class="invalid-feedback d-block register-field-feedback">
                      {{ fieldErrorText('email') }}
                    </div>
                  </div>
                  <div class="form-floating reg-password-field register-field">
                    <input
                      :type="showPassword ? 'text' : 'password'"
                      class="form-control"
                      :class="{ 'is-invalid': fieldInvalid('password') }"
                      id="password"
                      v-model="formData.password"
                      placeholder="Contraseña"
                      required
                      @input="clearServerFieldError('password')"
                    >
                    <label for="password">
                      <i class="fas fa-lock icon-reg me-2"></i>Contraseña
                    </label>
                    <span
                      class="password-toggle"
                      @click="showPassword = !showPassword"
                    >
                      <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                    </span>
                    <div v-if="fieldInvalid('password')" class="invalid-feedback d-block register-field-feedback">
                      {{ fieldErrorText('password') }}
                    </div>
                  </div>
                  <div class="form-floating reg-password-field register-field">
                    <input
                      :type="showConfirmPassword ? 'text' : 'password'"
                      class="form-control"
                      :class="{ 'is-invalid': fieldInvalid('password2') }"
                      id="confirmPassword"
                      v-model="formData.password2"
                      placeholder="Confirmar Contraseña"
                      required
                      @input="clearServerFieldError('password2')"
                    >
                    <label for="confirmPassword">
                      <i class="fas fa-lock icon-reg me-2"></i>Confirmar Contraseña
                    </label>
                    <span
                      class="password-toggle"
                      @click="showConfirmPassword = !showConfirmPassword"
                    >
                      <i :class="showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
                    </span>
                    <div v-if="fieldInvalid('password2')" class="invalid-feedback d-block register-field-feedback">
                      {{ fieldErrorText('password2') }}
                    </div>
                  </div>
              </div>
            </div>

            <!-- Mensaje de error -->
            <div v-if="errorMessage && !showEmailExistsError" class="alert alert-danger mb-3">
              {{ errorMessage }}
            </div>

            <!-- Email ya registrado y verificado: iniciar sesión -->
            <div v-if="showEmailExistsError" class="alert alert-success mb-3 small text-black">
              <p class="mb-3">Este email ya tiene una cuenta activa. Iniciá sesión con tu usuario y contraseña.</p>
              <button type="button" class="btn btn-outline-reg btn-sm" @click="goToLogin">
                Iniciar sesión
              </button>
            </div>

            <div class="step-actions">
              <button
                v-if="currentStepIndex > 0"
                type="button"
                class="btn btn-outline-reg w-100"
                @click="prevStep"
              >
                Atrás
              </button>
              <button
                v-if="currentStep === 'personal'"
                type="button"
                class="btn btn-reg-cta w-100"
                @click="nextStep"
                :disabled="isLoading"
              >
                Continuar
              </button>
              <button
                v-else
                type="submit"
                class="btn btn-reg-cta w-100"
                :disabled="isLoading"
              >
                <span v-if="isLoading" class="spinner-border spinner-border-sm me-2"></span>
                {{ isLoading ? 'Creando cuenta...' : 'Crear cuenta' }}
              </button>
            </div>

            <div class="text-center small mt-3">
              <router-link to="/iniciar-sesion" class="link-reg text-decoration-none">Iniciar sesión</router-link>
            </div>
          </form>
      </div>
    </div>
  </div>

</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, watch } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { required, minLength, email, helpers } from '@vuelidate/validators'
import { useRouter } from 'vue-router'
import { register, resendVerification } from '@/services/auth'
import { useToast } from 'vue-toastification'
import { saveCheckoutProfile } from '@/utils/checkoutProfileStorage'

const router = useRouter()
const toast = useToast()

// Estado del formulario - Corregido para usar password2
const formData = reactive({
  first_name: '',
  last_name: '',
  username: '',
  email: '',
  dni: '',
  phone: '',
  password: '',
  password2: '',
})

// Estado UI
const showPassword = ref(false)
const showConfirmPassword = ref(false)
const isLoading = ref(false)
const errorMessage = ref('')
const showEmailExistsError = ref(false)

const showVerification = ref(false)
const registeredEmail = ref('')
const activationRedirectCountdown = ref(15)
let activationRedirectTimer = null
/** Registro repetido o email pendiente: se reenvió link / pantalla de activación */
const registrationWasResend = ref(false)

/** Errores por campo devueltos por el API (DRF: { campo: ["msg"] }) */
const serverFieldErrors = ref({})

function clearServerFieldErrors() {
  serverFieldErrors.value = {}
}

function clearServerFieldError(field) {
  if (!serverFieldErrors.value[field]) return
  const next = { ...serverFieldErrors.value }
  delete next[field]
  serverFieldErrors.value = next
}

/**
 * Mapea respuestas 400 típicas de DRF a serverFieldErrors.
 * @returns {boolean} true si hubo errores de campo o mensaje general aplicado
 */
function applyDrfFieldErrors(data) {
  clearServerFieldErrors()
  if (!data || typeof data !== 'object') return false
  const fieldMap = {}
  const skip = new Set(['error', 'errors'])
  for (const [key, val] of Object.entries(data)) {
    if (skip.has(key)) continue
    if (key === 'detail') {
      if (typeof val === 'string' && val.trim()) {
        fieldMap._general = val.trim()
      } else if (Array.isArray(val) && val.length) {
        const t = val.map(String).filter(Boolean).join(' ')
        if (t) fieldMap._general = t
      }
      continue
    }
    if (key === 'non_field_errors' && Array.isArray(val)) {
      const t = val.map(String).filter(Boolean).join(' ')
      if (t) fieldMap._general = fieldMap._general ? `${fieldMap._general} ${t}` : t
      continue
    }
    if (Array.isArray(val) && val.length) {
      const t = val.map(String).filter(Boolean).join(' ')
      if (t) fieldMap[key] = t
    } else if (typeof val === 'string' && val.trim()) {
      fieldMap[key] = val.trim()
    }
  }
  if (Object.keys(fieldMap).length === 0) return false
  serverFieldErrors.value = fieldMap
  return true
}

// Validador personalizado para confirmar contraseña
const phoneDigitsOk = helpers.withMessage(
  'Ingresá un teléfono válido (mínimo 8 dígitos)',
  (value) => String(value || '').replace(/\D/g, '').length >= 8
)

const dniDigitsOk = helpers.withMessage(
  'Ingresá un DNI o CUIT válido (7 a 11 dígitos)',
  (value) => {
    const d = String(value || '').replace(/\D/g, '')
    return d.length >= 7 && d.length <= 11
  }
)

let persistDebounce
function persistCheckoutDraft() {
  if (!formData.dni && !formData.phone) return
  saveCheckoutProfile({
    dni_cuit: formData.dni,
    phone: formData.phone,
  })
}

function schedulePersistCheckoutDraft() {
  clearTimeout(persistDebounce)
  persistDebounce = setTimeout(persistCheckoutDraft, 800)
}

const headerTitle = computed(() => {
  if (showVerification.value) return 'Revisá tu email'
  return 'Crear cuenta'
})

const steps = [
  { key: 'personal', label: 'Tus datos' },
  { key: 'account', label: 'Tu cuenta' },
]

const stepHint = computed(() => {
  if (currentStep.value === 'personal') {
    return 'Completá tu nombre como figura en el DNI o CUIT y un teléfono de contacto.'
  }
  return 'El usuario será tu identificación en Fixglass; usá una contraseña segura y recordala.'
})

const currentStep = ref('personal')

const currentStepIndex = computed(() =>
  steps.findIndex((step) => step.key === currentStep.value)
)

const progressPercent = computed(() => {
  const idx = currentStepIndex.value
  if (idx < 0) return 0
  return Math.round(((idx + 1) / steps.length) * 100)
})

/** Formulario largo o verificación: alinear arriba y permitir scroll cómodo */
const registerViewPreferScroll = computed(() => true)

function stopActivationRedirectCountdown() {
  if (activationRedirectTimer) {
    clearInterval(activationRedirectTimer)
    activationRedirectTimer = null
  }
}

function goHomeNow() {
  stopActivationRedirectCountdown()
  router.push('/')
}

function showActivationLinkSent(email, wasResend = false) {
  registeredEmail.value = String(email || '').trim()
  registrationWasResend.value = wasResend
  showVerification.value = true
  errorMessage.value = ''
  showEmailExistsError.value = false
  clearServerFieldErrors()
  activationRedirectCountdown.value = 15
  stopActivationRedirectCountdown()

  activationRedirectTimer = setInterval(() => {
    activationRedirectCountdown.value -= 1
    if (activationRedirectCountdown.value <= 0) {
      goHomeNow()
    }
  }, 1000)
}

onMounted(() => {
  // Título manejado por el router
})

onUnmounted(() => {
  stopActivationRedirectCountdown()
})

watch(
  [() => formData.dni, () => formData.phone],
  () => schedulePersistCheckoutDraft()
)

const rules = computed(() => {
  const base = {
    first_name: {
      required: helpers.withMessage('El nombre es requerido', required),
      minLength: helpers.withMessage('El nombre debe tener al menos 2 caracteres', minLength(2))
    },
    last_name: {
      required: helpers.withMessage('El apellido es requerido', required),
      minLength: helpers.withMessage('El apellido debe tener al menos 2 caracteres', minLength(2))
    },
    username: {
      required: helpers.withMessage('El nombre de usuario es requerido', required),
      minLength: helpers.withMessage(
        'El nombre de usuario debe tener al menos 4 caracteres',
        minLength(4)
      )
    },
    email: {
      required: helpers.withMessage('El email es requerido', required),
      email: helpers.withMessage('Ingrese un email válido', email)
    },
    dni: {
      required: helpers.withMessage('El DNI o CUIT es requerido', required),
      dniDigitsOk,
    },
    phone: {
      required: helpers.withMessage('El teléfono es requerido', required),
      phoneDigitsOk
    },
    password: {
      required: helpers.withMessage('La contraseña es requerida', required),
      minLength: helpers.withMessage(
        'La contraseña debe tener al menos 6 caracteres',
        minLength(6)
      )
    },
    password2: {
      required: helpers.withMessage('Debe confirmar la contraseña', required),
      sameAsPwd: helpers.withMessage(
        'Las contraseñas no coinciden',
        (value) => value === formData.password
      )
    }
  }
  return base
})

const v$ = useVuelidate(rules, formData)

const stepFields = {
  personal: ['first_name', 'last_name', 'dni', 'phone'],
  account: ['username', 'email', 'password', 'password2'],
}

function fieldInvalid(field) {
  if (serverFieldErrors.value[field]) return true
  const s = v$.value[field]
  return !!(s && s.$error)
}

function fieldErrorText(field) {
  const s = v$.value[field]
  if (s && s.$error && s.$errors?.length) return s.$errors[0].$message
  return serverFieldErrors.value[field] || ''
}

async function validateStep(stepKey) {
  const fields = stepFields[stepKey] || []
  let ok = true
  for (const field of fields) {
    const st = v$.value[field]
    if (!st) continue
    const result = await st.$validate()
    if (!result) ok = false
  }
  if (!ok) {
    for (const field of fields) {
      const st = v$.value[field]
      if (st && st.$error && st.$errors?.length) {
        toast.error(st.$errors[0].$message)
        break
      }
    }
  }
  return ok
}

async function nextStep() {
  if (isLoading.value) return
  const stepKey = currentStep.value
  const ok = await validateStep(stepKey)
  if (!ok) return
  const idx = currentStepIndex.value
  if (idx < steps.length - 1) {
    currentStep.value = steps[idx + 1].key
  }
}

function prevStep() {
  const idx = currentStepIndex.value
  if (idx > 0) {
    currentStep.value = steps[idx - 1].key
  }
}

/**
 * Cuenta pendiente de verificación: reenvía link y abre el aviso de activación.
 * @returns {true} ok | {'verified'} cuenta ya activa | {false} otro error
 */
async function tryOpenPendingVerification(email) {
  const clean = String(email || '').trim()
  if (!clean) return false
  try {
    const result = await resendVerification(clean)
    if (result?.error) {
      const er = typeof result.error === 'string' ? result.error : ''
      if (er.includes('verificada')) return 'verified'
      toast.error(er || 'No se pudo enviar el link de activación')
      return false
    }
    formData.email = clean
    showActivationLinkSent(clean, true)
    toast.info(
      'Este correo ya tiene una cuenta pendiente de verificar. Te enviamos un nuevo link de activación.'
    )
    return true
  } catch (error) {
    const raw = error.response?.data?.error
    const msg = typeof raw === 'string' ? raw : ''
    if (msg.includes('verificada')) return 'verified'
    if (error.response?.status === 404) {
      toast.error('No encontramos una cuenta con ese email.')
      return false
    }
    if (msg) toast.error(msg)
    else toast.error('No se pudo enviar el link de activación.')
    return false
  }
}

function isDuplicateEmailServerMessage(text) {
  const s = String(text || '')
  return (
    s.includes('Este email ya está registrado') ||
    s.includes('Ya existe una cuenta') ||
    s.includes('registrada con este email')
  )
}

// Manejo del submit actualizado y corregido
const handleSubmit = async () => {
  if (isLoading.value) return
  try {
    errorMessage.value = ''
    showEmailExistsError.value = false
    clearServerFieldErrors()

    const isFormValid = await v$.value.$validate()

    if (!isFormValid) {
      const keys = Object.keys(rules.value)
      for (const key of keys) {
        const st = v$.value[key]
        if (st && st.$error && st.$errors?.length) {
          toast.error(st.$errors[0].$message)
          break
        }
      }
      return
    }

    isLoading.value = true

    const response = await register(formData)
      .then((res) => {
        if (res && res.resend) {
          showActivationLinkSent(formData.email, true)
          persistCheckoutDraft()
          toast.info('Ya existe una cuenta con este email sin verificar. Te reenviamos el link de activación.')
          return true
        }
        showActivationLinkSent(formData.email, false)
        persistCheckoutDraft()
        toast.success('Cuenta creada. Te enviamos un link para activarla.')
        return true
      })
      .catch(async (error) => {
        console.error('Error de registro:', error)

        if (error?.isTimeout) {
          toast.error(
            error.message ||
              'La solicitud tardó demasiado. Si ya recibiste el correo de verificación, revisá tu email antes de reintentar.'
          )
          return false
        }
        if (error?.isNetworkError) {
          toast.error(error.message || 'No se pudo conectar con el servidor.')
          return false
        }

        const backendErrors = error.response?.data
        if (backendErrors && typeof backendErrors === 'object') {
          const errorString = JSON.stringify(backendErrors)

          if (isDuplicateEmailServerMessage(errorString)) {
            const r = await tryOpenPendingVerification(formData.email)
            if (r === true) return false
            if (r === 'verified') {
              showEmailExistsError.value = true
              errorMessage.value = 'Este email ya está registrado'
              return false
            }
          }

          if (applyDrfFieldErrors(backendErrors)) {
            const emailErr = serverFieldErrors.value.email
            if (emailErr && isDuplicateEmailServerMessage(emailErr)) {
              const r = await tryOpenPendingVerification(formData.email)
              if (r === true) return false
              if (r === 'verified') {
                showEmailExistsError.value = true
                errorMessage.value = 'Este email ya está registrado'
                return false
              }
            }
            const fields = Object.keys(serverFieldErrors.value).filter((k) => k !== '_general')
            if (fields.length) {
              toast.error(serverFieldErrors.value[fields[0]])
            } else if (serverFieldErrors.value._general) {
              errorMessage.value = serverFieldErrors.value._general
              toast.error(serverFieldErrors.value._general)
            }
            return false
          }

          if (Array.isArray(backendErrors.error)) {
            const emailExistsError = backendErrors.error.find((msg) =>
              isDuplicateEmailServerMessage(msg)
            )
            if (emailExistsError) {
              const r = await tryOpenPendingVerification(formData.email)
              if (r === true) return false
              if (r === 'verified') {
                showEmailExistsError.value = true
                errorMessage.value = emailExistsError
                return false
              }
              showEmailExistsError.value = true
              errorMessage.value = emailExistsError
              return false
            }
            backendErrors.error.forEach((msg) => toast.error(msg))
            errorMessage.value = backendErrors.error.join(' ')
            return false
          }

          if (Array.isArray(backendErrors.errors)) {
            const emailExistsError = backendErrors.errors.find((msg) =>
              isDuplicateEmailServerMessage(msg)
            )
            if (emailExistsError) {
              const r = await tryOpenPendingVerification(formData.email)
              if (r === true) return false
              if (r === 'verified') {
                showEmailExistsError.value = true
                errorMessage.value = emailExistsError
                return false
              }
              showEmailExistsError.value = true
              errorMessage.value = emailExistsError
              return false
            }
            backendErrors.errors.forEach((msg) => toast.error(msg))
            errorMessage.value = backendErrors.errors.join(' ')
            return false
          }

          if (typeof backendErrors.error === 'string') {
            if (isDuplicateEmailServerMessage(backendErrors.error)) {
              const r = await tryOpenPendingVerification(formData.email)
              if (r === true) return false
              if (r === 'verified') {
                showEmailExistsError.value = true
                errorMessage.value = backendErrors.error
                return false
              }
              showEmailExistsError.value = true
              errorMessage.value = backendErrors.error
              return false
            }
            toast.error(backendErrors.error)
            errorMessage.value = backendErrors.error
            return false
          }

          if (typeof backendErrors.detail === 'string') {
            toast.error(backendErrors.detail)
            errorMessage.value = backendErrors.detail
            return false
          }

          toast.error('No pudimos procesar la respuesta del servidor.')
          errorMessage.value = 'Revisá los datos e intentá de nuevo.'
          return false
        }

        const msg =
          typeof error?.message === 'string' && error.message && !error.response
            ? error.message
            : 'No pudimos completar el registro. Revisá los datos o tu conexión.'
        toast.error(msg)
        return false
      })

    if (response !== true) {
      return
    }
  } catch (error) {
    console.error('Error en el manejo de la solicitud de registro:', error)
    toast.error('Error inesperado. Por favor, intenta nuevamente.')
    errorMessage.value = 'Error inesperado al procesar la solicitud'
  } finally {
    isLoading.value = false
  }
}

const goToLogin = () => {
  router.push('/iniciar-sesion');
}

const resetEmailExistsError = () => {
  showEmailExistsError.value = false
  errorMessage.value = ''
  clearServerFieldError('email')
}
</script>

<style scoped>
/* Paleta neutra + acento marca (verde); evita tintes rojos en bordes y detalles */
.register-view {
  --reg-black: #111111;
  --reg-brand: var(--primary-color);
  --reg-brand-dark: var(--primary-dark);
  --reg-brand-soft: var(--brand-100);
  --reg-detail: #4a5568;
  --reg-border: #e2e8f0;
  --reg-muted-line: rgba(15, 23, 42, 0.08);
  --reg-surface-soft: #f1f5f9;
  /* NavbarComponent fija: evitar que el form quede tapado (variable en :root) */
  --reg-nav-offset: var(--navbar-total-height, 72px);
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-size: cover;
  background-position: center;
  box-sizing: border-box;
  padding: 1.25rem;
  padding-top: calc(var(--reg-nav-offset) + 1.25rem);
  padding-bottom: 2rem;
}

.register-shell {
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
}

.register-shell--wide {
  max-width: 820px;
}

.register-view--form-scroll {
  align-items: flex-start;
}

.register-card {
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.65);
  border-radius: 15px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  transition: box-shadow 0.3s ease, transform 0.3s ease;
}

.register-card:hover {
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.12);
}

.register-card--standard {
  padding: 2rem 2.25rem 2.25rem;
}

@media (min-width: 768px) {
  .register-card--standard {
    padding-left: 2.5rem;
    padding-right: 2.5rem;
  }
}

.reg-password-field {
  position: relative;
}

.register-fields {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.125rem;
  width: 100%;
}

.register-fields--tight-after-title {
  margin-top: 0;
}

.register-field {
  min-width: 0;
}

.register-panel-title + .register-fields {
  margin-top: 0;
}

.register-card--account-choice {
  padding: 2.75rem 2.25rem 2.5rem;
  min-height: min(78vh, 560px);
  display: flex;
  flex-direction: column;
}

.register-subtitle {
  font-size: 0.95rem;
  line-height: 1.45;
  max-width: 28rem;
  margin-left: auto;
  margin-right: auto;
  color: var(--reg-detail);
}

.register-step-hint {
  font-size: 0.875rem;
  line-height: 1.5;
  max-width: 26rem;
  margin-left: auto;
  margin-right: auto;
  color: #64748b;
}

.register-panel-title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--reg-black);
  margin-bottom: 1rem;
  padding-bottom: 0.65rem;
  border-bottom: 1px solid var(--reg-border);
  display: flex;
  align-items: center;
}

.register-panel-title i {
  color: var(--reg-brand);
  opacity: 0.95;
  font-size: 0.95em;
}

.account-choice {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.account-choice-row {
  flex: 1;
  align-content: stretch;
}

.account-choice-footer {
  margin-top: auto;
  padding-top: 1.75rem;
}

.register-title {
  color: var(--reg-black);
  font-weight: 700;
  letter-spacing: -0.02em;
  font-size: 1.65rem;
}

.register-card--account-choice .register-title {
  font-size: 1.8rem;
}

.register-body {
  color: var(--reg-black);
}

.register-detail {
  color: var(--reg-detail);
}

.icon-reg {
  color: var(--reg-brand);
  opacity: 0.9;
}

.link-reg {
  color: var(--reg-brand);
  font-weight: 600;
}

.register-card .btn.btn-link.link-reg {
  color: var(--reg-brand) !important;
  font-weight: 600;
}

.link-reg:hover,
.register-card .btn.btn-link.link-reg:hover {
  color: var(--reg-brand-dark) !important;
  text-decoration: underline !important;
}

.link-reg-back {
  color: var(--reg-detail);
  font-weight: 500;
}

.link-reg-back:hover {
  color: var(--reg-brand-dark);
}

.btn-reg-cta {
  background: var(--reg-brand);
  border: none;
  color: #fff;
  font-weight: 600;
  transition: background 0.2s ease, transform 0.15s ease, box-shadow 0.2s ease;
}

.btn-reg-cta:hover:not(:disabled) {
  background: var(--reg-brand-dark);
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(27, 127, 76, 0.35);
}

.btn-reg-cta:active:not(:disabled) {
  transform: translateY(0);
}

.btn-reg-cta:disabled {
  opacity: 0.65;
}

.btn-reg-cta .spinner-border {
  border-color: rgba(255, 255, 255, 0.35);
  border-right-color: #fff;
}

.btn-outline-reg {
  color: var(--reg-brand-dark);
  border-color: var(--reg-brand);
  background: transparent;
  font-weight: 600;
}

.btn-outline-reg:hover {
  background: var(--reg-brand-soft);
  color: var(--reg-brand-dark);
  border-color: var(--reg-brand);
}

.register-card .form-floating > .form-control {
  color: var(--reg-black);
  background: #fff;
  border-color: var(--reg-border);
  padding-right: 2.5rem;
  min-height: calc(3.5rem + 2px);
  height: calc(3.5rem + 2px);
}

.register-card .form-floating > .form-control:focus {
  border-color: var(--reg-brand);
  box-shadow: 0 0 0 0.2rem rgba(27, 127, 76, 0.2);
}

.register-card .form-floating > label {
  color: var(--reg-detail);
}

.register-card .form-floating > .form-control:focus ~ label,
.register-card .form-floating > .form-control:not(:placeholder-shown) ~ label {
  color: var(--reg-brand-dark);
}

.password-toggle {
  position: absolute;
  top: 50%;
  right: 1rem;
  transform: translateY(-50%);
  cursor: pointer;
  color: var(--reg-detail);
  z-index: 10;
}

.password-toggle:hover {
  color: var(--reg-brand-dark);
}

.register-card .invalid-feedback.register-field-feedback {
  margin-top: 0.2rem;
  line-height: 1.3;
}

.register-card .form-floating > .form-control.is-invalid {
  border-color: #94a3b8;
}

.register-card .form-floating > .form-control.is-invalid:focus {
  border-color: #64748b;
  box-shadow: 0 0 0 0.18rem rgba(100, 116, 139, 0.25);
}

.register-card .invalid-feedback {
  color: #57534e;
}

.register-card form.needs-validation.w-100 {
  max-width: 100%;
}

@media (max-width: 576px) {
  .register-view {
    --reg-nav-offset: var(--navbar-total-height-mobile, var(--navbar-total-height, 72px));
    padding-left: 0.75rem;
    padding-right: 0.75rem;
    padding-bottom: 1.5rem;
    padding-top: calc(var(--reg-nav-offset) + 0.75rem);
    align-items: flex-start;
  }

  .register-card--standard {
    padding: 1.5rem 1.25rem 1.75rem;
  }

  .register-card--account-choice {
    min-height: auto;
    padding: 1.75rem 1.25rem 1.5rem;
  }

  .register-card--account-choice .register-title {
    font-size: 1.5rem;
  }

  .account-choice-footer {
    padding-top: 1.35rem;
  }
}

.verification-code-input input {
  font-size: 1.5rem;
  letter-spacing: 0.5rem;
  text-align: center;
  color: var(--reg-black);
}

.register-progress {
  display: grid;
  gap: 0.6rem;
}

.register-progress__bar {
  height: 6px;
  border-radius: 999px;
  background: var(--reg-muted-line);
  overflow: hidden;
}

.register-progress__fill {
  display: block;
  height: 100%;
  background: var(--reg-brand);
  transition: width 0.25s ease;
}

.register-progress__steps {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  font-size: 0.7rem;
  color: var(--reg-detail);
  text-transform: none;
  letter-spacing: 0.02em;
  font-weight: 600;
}

.register-progress__step {
  flex: 1;
  flex-direction: column;
  align-items: center;
  text-align: center;
  opacity: 0.45;
  display: flex;
  gap: 0.35rem;
  min-width: 0;
}

.register-progress__step-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.85rem;
  height: 1.85rem;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 700;
  background: var(--reg-surface-soft);
  color: var(--reg-detail);
  border: 2px solid transparent;
  transition:
    background 0.2s ease,
    color 0.2s ease,
    border-color 0.2s ease,
    transform 0.15s ease;
}

.register-progress__step-label {
  line-height: 1.25;
  font-size: 0.68rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.register-progress__step.is-active {
  opacity: 1;
  color: var(--reg-brand-dark);
}

.register-progress__step.is-active .register-progress__step-num {
  background: var(--reg-brand-soft);
  color: var(--reg-brand-dark);
  border-color: var(--reg-brand);
  transform: scale(1.06);
}

.register-progress__step.is-done {
  opacity: 0.88;
}

.register-progress__step.is-done .register-progress__step-num {
  background: var(--reg-brand);
  color: #fff;
  border-color: var(--reg-brand-dark);
}

.register-progress__step.is-done .register-progress__step-label {
  color: var(--reg-brand-dark);
}

.step-actions {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.75rem;
  margin-top: 0.5rem;
  margin-bottom: 1rem;
}

.choice-tile {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-height: 11.5rem;
  padding: 1.5rem 1.25rem;
  border-radius: 12px;
  border: 2px solid #e9ecef;
  background: rgba(255, 255, 255, 0.92);
  color: inherit;
  cursor: pointer;
  height: 100%;
  overflow: hidden;
  transition: border-color 0.2s ease, background 0.2s ease, transform 0.15s ease, box-shadow 0.2s ease;
}

.choice-tile::after {
  content: '';
  position: absolute;
  inset: auto 0 0 0;
  height: 3px;
  background: linear-gradient(90deg, rgba(27, 127, 76, 0.1), rgba(27, 127, 76, 0.55));
  opacity: 0;
  transition: opacity 0.2s ease;
}

.choice-tile:hover {
  border-color: var(--reg-brand);
  background: var(--reg-brand-soft);
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(27, 127, 76, 0.12);
}

.choice-tile:hover::after {
  opacity: 1;
}

.choice-tile:focus-visible {
  outline: 2px solid var(--reg-brand);
  outline-offset: 2px;
}

.choice-tile-icon {
  width: 2.75rem;
  height: 2.75rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.8rem;
  font-size: 1.2rem;
  color: var(--reg-brand);
  margin-bottom: 0.65rem;
  background: rgba(27, 127, 76, 0.12);
}

.choice-tile-title {
  font-weight: 700;
  font-size: 1.28rem;
  color: var(--reg-black);
  margin-bottom: 0.35rem;
}

.choice-tile-desc {
  font-size: 0.9rem;
  color: var(--reg-detail);
  line-height: 1.45;
  font-weight: 500;
  margin-bottom: 0.65rem;
  flex: 1;
}

.choice-cta {
  margin-top: auto;
  display: inline-flex;
  align-items: center;
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--reg-brand-dark);
  padding-top: 0.5rem;
}

/* Alertas: error suave sin rojo intenso; éxito con acento verde */
.register-card .alert-danger {
  color: var(--reg-black);
  border-color: #e7d4d4;
  background: #faf7f7;
}

.register-card .alert-success {
  color: var(--reg-black);
  background: var(--reg-brand-soft);
  border: 1px solid var(--reg-brand);
}

.register-card .alert-success .btn-outline-reg {
  margin-top: 0.25rem;
}

.activation-sent {
  text-align: center;
}

.activation-sent__icon {
  width: 4.25rem;
  height: 4.25rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.25rem;
  border-radius: 999px;
  color: var(--reg-brand-dark);
  background: var(--reg-brand-soft);
  border: 1px solid rgba(27, 127, 76, 0.28);
  font-size: 1.65rem;
}

.activation-sent__copy {
  color: var(--reg-detail);
  font-weight: 500;
  line-height: 1.55;
}

.activation-sent__timer {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2.5rem;
  padding: 0.6rem 1rem;
  border-radius: 999px;
  background: #f4f4f5;
  color: var(--reg-black);
  font-weight: 600;
}

@media (max-width: 576px) {
  .choice-tile {
    min-height: 0;
    padding: 1.2rem 1rem;
  }
}

/* ── Distributor pending modal ── */
.dist-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
}

.dist-modal-box {
  position: relative;
  background: #ffffff;
  border-radius: 1.25rem;
  padding: 2rem 1.75rem 1.75rem;
  max-width: 440px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18);
  border: 1px solid #e5e7eb;
  max-height: 90vh;
  overflow-y: auto;
}

.dist-modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: #f3f4f6;
  border: none;
  border-radius: 50%;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #6b7280;
  font-size: 0.85rem;
  transition: background 0.15s ease, color 0.15s ease;
}

.dist-modal-close:hover {
  background: #e5e7eb;
  color: #111111;
}

.dist-modal-icon-wrap {
  text-align: center;
  margin-bottom: 1rem;
}

.dist-modal-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 4rem;
  height: 4rem;
  border-radius: 50%;
  background: #dff6e8;
  color: var(--primary-color);
  font-size: 1.5rem;
}

.dist-modal-title {
  text-align: center;
  font-size: 1.25rem;
  font-weight: 700;
  color: #111111;
  margin: 0 0 0.25rem 0;
}

.dist-modal-subtitle {
  text-align: center;
  font-size: 0.95rem;
  color: #6b7280;
  margin: 0 0 1.25rem 0;
}

.dist-modal-cta:hover {
  background: var(--reg-brand-dark);
  transform: translateY(-1px);
}

/* Transition */
.dist-modal-enter-active,
.dist-modal-leave-active {
  transition: opacity 0.25s ease;
}

.dist-modal-enter-active .dist-modal-box,
.dist-modal-leave-active .dist-modal-box {
  transition: transform 0.25s ease, opacity 0.25s ease;
}

.dist-modal-enter-from,
.dist-modal-leave-to {
  opacity: 0;
}

.dist-modal-enter-from .dist-modal-box,
.dist-modal-leave-to .dist-modal-box {
  transform: translateY(16px) scale(0.97);
  opacity: 0;
}

</style>
